import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchCountryPaymentGateways, createCountryPaymentGateway, updateCountryPaymentGateway, deleteCountryPaymentGateway } from "../services/countryPaymentGatewayService";

/* Fetch */
export const getCountryPaymentGateways = createAsyncThunk(
    "countryPaymentGateway/getAll",
    async (params = {}, { rejectWithValue }) => {
        try {
            return await fetchCountryPaymentGateways(params);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to fetch country payment gateways."
            );
        }
    }
);

/* Create */
export const addCountryPaymentGateway = createAsyncThunk(
    "countryPaymentGateway/create",
    async (payload, { rejectWithValue }) => {
        try {
            return await createCountryPaymentGateway(payload);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to create configuration."
            );
        }
    }
);

/* Update */
export const editCountryPaymentGateway = createAsyncThunk(
    "countryPaymentGateway/update",
    async ({ id, payload }, { rejectWithValue }) => {
        try {
            return await updateCountryPaymentGateway(id, payload);
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to update configuration."
            );
        }
    }
);

/* Delete */
export const removeCountryPaymentGateway = createAsyncThunk(
    "countryPaymentGateway/delete",
    async (id, { rejectWithValue }) => {
        try {
            await deleteCountryPaymentGateway(id);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.message || "Failed to delete configuration."
            );
        }
    }
);

const initialState = {
    countryPaymentGateways: [],
    status: "idle",
    error: null,
    pagination: {
        page: 1,
        page_size: 20,
        total_pages: 0,
        total_count: 0,
        count: 0,
    },
    filters: {},
};

const countryPaymentGatewaySlice = createSlice({
    name: "countryPaymentGateway",
    initialState,
    reducers: {
        setFilters(state, action) {
            state.filters = action.payload;
        },
        resetFilters(state) {
            state.filters = {};
        },
    },
    extraReducers: (builder) => {
        builder
            /* Fetch */
            .addCase(getCountryPaymentGateways.pending, (state) => {
                state.status = "loading";
                state.error = null;
            })
            .addCase(getCountryPaymentGateways.fulfilled, (state, action) => {
                state.status = "succeeded";
                state.countryPaymentGateways = action.payload.results || [];
                state.pagination = {
                    count: action.payload.count,
                    total_pages: action.payload.total_pages,
                    current_page: action.payload.current_page,
                    page_size: action.payload.page_size,
                };
            })
            .addCase(getCountryPaymentGateways.rejected, (state, action) => {
                state.status = "failed";
                state.error = action.payload;
            })

            /* Create */
            .addCase(addCountryPaymentGateway.fulfilled, (state, action) => {
                state.countryPaymentGateways.unshift(action.payload.data);
            })

            /* Update */
            .addCase(editCountryPaymentGateway.fulfilled, (state, action) => {
                const index = state.countryPaymentGateways.findIndex(
                    (item) => item.id === action.payload.data.id
                );

                if (index !== -1) {
                    state.countryPaymentGateways[index] = action.payload.data;
                }
            })

            /* Delete */
            .addCase(removeCountryPaymentGateway.fulfilled, (state, action) => {
                state.countryPaymentGateways = state.countryPaymentGateways.filter(
                    (item) => item.id !== action.payload
                );
            });
    },
});

export const { setFilters, resetFilters } = countryPaymentGatewaySlice.actions;
export default countryPaymentGatewaySlice.reducer;